import SCSS_EXPORTS from './scss/media.module.scss';
import throttle from 'lodash/throttle';

export const isTabletMaxWidth = () => {
    return window.matchMedia(`(max-width: ${SCSS_EXPORTS.tablet})`).matches;
};

export const isLaptopMaxWidth = () => {
    return window.matchMedia(`(max-width: ${SCSS_EXPORTS.laptop})`).matches;
};

export const calcDisplaySafeHeight = (() => {
    let initialized = false;
    let lastHeight = 0;

    const update = () => {
        const currentHeight = window.innerHeight;

        if (currentHeight !== lastHeight) {
            lastHeight = currentHeight;
            document.documentElement.style.setProperty('--display-safe-height', `${currentHeight}px`);
        }
    };

    const handleResize = () => {
        throttle(update, 100);
    };

    const disconnect = () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
        initialized = false;
    };

    return () => {
        if (initialized) {
            return {
                disconnect,
            };
        }

        initialized = true;
        lastHeight = window.innerHeight;

        update();

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return {
            disconnect,
        };
    };
})();

export const setPositionRelativeElement = (target, relativeElement) => {
    if (!target || !(target instanceof HTMLElement)) {
        return false;
    }

    if (!relativeElement || !(relativeElement instanceof HTMLElement)) {
        return false;
    }

    const relativeElementOriginalRect = relativeElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const windowWidth = document.documentElement.clientWidth;
    const spaceBelow = viewportHeight - relativeElementOriginalRect.bottom;
    const spaceAbove = relativeElementOriginalRect.top;
    const spaceRight = windowWidth - relativeElementOriginalRect.right;
    const spaceLeft = relativeElementOriginalRect.left;
    const offset = 2;

    target?.style?.setProperty('min-width', `${relativeElementOriginalRect.width}px`);
    target?.style.setProperty('--select-width', `${relativeElementOriginalRect.width}px`);

    const setTopPosition = () => {
        target?.style?.setProperty('top', 'auto');
        target?.style?.setProperty('bottom', `${viewportHeight - relativeElementOriginalRect.top + offset}px`);
    };

    const setBottomPosition = () => {
        target?.style?.setProperty('bottom', 'auto');
        target?.style?.setProperty('top', `${relativeElementOriginalRect.bottom + offset}px`);
    };

    const setLeftPosition = () => {
        target?.style?.setProperty('left', `${relativeElementOriginalRect.left}px`);
        target?.style?.removeProperty('right');
    };

    const setRightPosition = () => {
        target?.style?.setProperty('left', 'auto');
        target?.style?.setProperty('right', `${spaceRight}px`);
    };

    requestAnimationFrame(() => {
        const contentHeight = target?.offsetHeight || target?.scrollHeight;
        const contentWidth = target?.offsetWidth || target?.scrollWidth;
        const fitsBelow = spaceBelow >= contentHeight + offset;

        if (target?.hasAttribute('items-top')) {
            setTopPosition();
        } else if (target?.hasAttribute('items-bottom')) {
            setBottomPosition();
        } else if (!fitsBelow && spaceAbove > spaceBelow) {
            setTopPosition();
        } else {
            setBottomPosition();
        }

        if (target?.hasAttribute('items-left')) {
            setLeftPosition();
        } else if (target?.hasAttribute('items-right')) {
            setRightPosition();
        } else if (windowWidth - contentWidth < spaceLeft) {
            setRightPosition();
        } else {
            setLeftPosition();
        }
    });
};

/**
 * Utility function that removes an event listener from a target before adding it
 *
 * @param target
 * @param event
 * @param listener
 * @param options
 */
export const replaceEventListener = (target, event, listener, options = {}) => {
    target.removeEventListener(event, listener, { capture: options?.capture || false });
    target.addEventListener(event, listener, options);
};
